import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tag, ArrowLeft, RefreshCcw, Plus } from 'lucide-react';
import alertService from '../services/alertService';
import { productService } from '../services/productService';
import { PageResponse } from '../types/api';
import { ProductPriceResponseDTO, ProductResponseDTO } from '../types/product';
import PriceModal from '../components/prices/PriceModal';

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString || '-';
  }
};

const ProductPriceHistoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<ProductResponseDTO | null>(null);
  const [pageData, setPageData] = useState<PageResponse<ProductPriceResponseDTO> | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [prodResp, pricesResp] = await Promise.all([
        productService.getById(id),
        productService.getPriceHistory(id, { page, size }),
      ]);
      const prodRaw: any = prodResp?.data;
      const prodData: any = prodRaw && prodRaw.data ? prodRaw.data : prodRaw;
      setProduct(prodData || null);
      const pricesRaw: any = pricesResp?.data;
      const pricesData: any = pricesRaw && pricesRaw.data ? pricesRaw.data : pricesRaw;
      setPageData(pricesData || null);
    } catch (err: any) {
      alertService.error('Erro ao carregar histórico de preços', err?.message || 'Tente novamente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id, page, size]);

  const items = useMemo(() => pageData?.content || [], [pageData]);
  const handleSaved = (saved: ProductPriceResponseDTO) => { setShowModal(false); setPage(0); load(); };

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(`/produtos/${id}/visualizar`)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Tag className="h-6 w-6 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Preços do Produto</h1>
            <p className="text-sm text-gray-600">
              Histórico de preços e vigência{product ? ` — ${product.codigo} • ${product.nome}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="berry-button-secondary inline-flex items-center gap-2" onClick={load} disabled={loading}>
            <RefreshCcw className="h-4 w-4" />
            Recarregar
          </button>
          <button className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg" onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4" />
            Novo Preço
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-700">
            {product ? (
              <>
                Produto: <span className="font-medium text-gray-900">{product.codigo}</span> — {product.nome}
              </>
            ) : (
              'Carregando produto...'
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Itens por página</label>
            <select value={size} onChange={(e) => { setPage(0); setSize(Number(e.target.value)); }} className="px-2 py-1 border rounded">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venda</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Custo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-600" colSpan={5}>Carregando...</td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-600" colSpan={5}>Nenhum preço encontrado.</td>
                </tr>
              )}
              {!loading && items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {product
                      ? `${product.codigo ? `${product.codigo} - ` : ''}${product.nome}`
                      : `${item.produtoCodigo ? `${item.produtoCodigo} - ` : ''}${item.produtoNome || item.produtoId}` }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(item.precoVenda)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatCurrency(item.precoCusto)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {formatDate(item.dataInicio)}{item.dataFim ? ` até ${formatDate(item.dataFim)}` : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${item.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {item.ativo ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pageData && pageData.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(page - 1, 0))}
                disabled={page === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(Math.min(page + 1, pageData.totalPages - 1))}
                disabled={page >= pageData.totalPages - 1}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">{Math.min(page * size + 1, pageData.totalElements)}</span>
                  {' '}até{' '}
                  <span className="font-medium">{Math.min((page + 1) * size, pageData.totalElements)}</span>
                  {' '}de{' '}
                  <span className="font-medium">{pageData.totalElements}</span>
                  {' '}resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(Math.max(page - 1, 0))}
                    disabled={page === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ‹
                  </button>
                  {Array.from({ length: pageData.totalPages }, (_, i) => i).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        p === page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {p + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(page + 1, pageData.totalPages - 1))}
                    disabled={page >= pageData.totalPages - 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ›
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      {showModal && (
        <PriceModal
          onClose={() => setShowModal(false)}
          onSave={handleSaved}
          defaultProdutoId={id}
          lockProdutoId
        />
      )}
    </div>
  );
};

export default ProductPriceHistoryPage;
